import Layout from '@/layouts/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ChatSummaryDetailModal from '@/components/chat/ChatSummaryDetailModal';
import { cn } from '@/lib/utils';
import { CalendarDays, ShieldAlert, MessageCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Heading } from '@/components/ui/heading';
import { breadcrumbs } from '@/components/ui/breadcrumb';
import { Patient } from '@domain/patient';

type TherapistChatSummary = {
  topics?: string[];
  concerns?: string[];
  goals?: string[];
  progress?: string[];
  challenges?: string[];
  successes?: string[];
  description?: string;
};

type TherapistChat = {
  id: number;
  title?: string | null;
  created_at: string;
  updated_at?: string;
  private: boolean;
  can_view_messages: boolean;
  summary?: TherapistChatSummary | null;
  review_failed_at?: string | null;
  summary_unavailable_message?: string | null;
};

type TherapistChatMessage = {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  image_url?: string | null;
  filename?: string | null;
  content_type?: string | null;
};

type Props = {
  chat: TherapistChat;
  messages: TherapistChatMessage[];
  patient: Patient;
};

function formatTimestamp(value?: string) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function MessageBubble({ message, name }: { message: TherapistChatMessage; name: string }) {
  const isAssistant = message.role === 'assistant';
  const label = isAssistant ? 'EmoviBot' : (name ?? 'Cliente');

  return (
    <li className={cn('flex', isAssistant ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'max-w-[70ch] rounded-2xl px-4 py-3 shadow-sm border',
          isAssistant
            ? 'bg-background border-border text-foreground'
            : 'bg-primary text-primary-foreground border-primary/70',
        )}
      >
        <div className="flex items-center justify-between gap-3 mb-2">
          <Badge variant={isAssistant ? 'outline' : 'secondary'}>{label}</Badge>
          <span className="text-[11px] text-muted-foreground">
            {formatTimestamp(message.created_at)}
          </span>
        </div>
        <p className="whitespace-pre-wrap text-sm leading-6">{message.content}</p>
        {message.image_url && (
          <div className="mt-3 text-xs">
            <a
              className="underline text-muted-foreground hover:text-foreground"
              href={message.image_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {message.filename || 'Allegato'}
            </a>
          </div>
        )}
      </div>
    </li>
  );
}

function PlaceholderMessages() {
  const skeletonRow = (index: number, align: 'left' | 'right'): ReactNode => (
    <div key={index} className={cn('flex', align === 'left' ? 'justify-start' : 'justify-end')}>
      <div className="w-full max-w-[60ch] space-y-2 rounded-2xl border border-border/80 bg-muted/70 p-4 opacity-80">
        <div className="h-3 w-20 rounded bg-muted-foreground/30" />
        <div className="h-4 w-5/6 rounded bg-muted-foreground/20" />
        <div className="h-4 w-4/6 rounded bg-muted-foreground/20" />
      </div>
    </div>
  );

  return (
    <div className="pointer-events-none select-none space-y-4 blur-sm">
      {skeletonRow(1, 'left')}
      {skeletonRow(2, 'right')}
      {skeletonRow(3, 'left')}
      {skeletonRow(4, 'right')}
    </div>
  );
}

export default function Show({ patient, chat, messages }: Props) {
  const summary = (chat.summary ?? null) as TherapistChatSummary | null;
  const summaryUnavailable = !!chat.review_failed_at;
  const formattedDate = formatTimestamp(chat.created_at);

  return (
    <Layout isAdmin>
      <div className="w-full space-y-8">
        {breadcrumbs([
          { text: 'Clienti', url: '/therapists/patients' },
          {
            text: patient.display_name,
            url: `/therapists/patients/${patient.id}`,
          },
          {
            text: 'Chat',
            url: `/therapists/patients/${patient.id}/chats`,
          },
          {
            text: chat.title ?? 'Chat',
            current: true,
          },
        ])}
        <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Heading level={1}>{chat.title?.trim() || `Chat #${chat.id}`}</Heading>
              {chat.private && (
                <Badge variant="destructive" className="uppercase tracking-tight">
                  Chat privata
                </Badge>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarDays className="h-4 w-4" aria-hidden />
              <span>{formattedDate}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ChatSummaryDetailModal
              chatId={chat.id}
              chatTitle={chat.title?.trim() || `Chat #${chat.id}`}
              summaryUnavailable={summaryUnavailable}
              unavailableMessage={chat.summary_unavailable_message}
              summary={
                summary
                  ? {
                      topics: summary.topics ?? [],
                      concerns: summary.concerns ?? [],
                      goals: summary.goals ?? [],
                      progress: summary.progress ?? [],
                      challenges: summary.challenges ?? [],
                      successes: summary.successes ?? [],
                      description: summary.description ?? '',
                    }
                  : null
              }
              generateEndpoint={summaryUnavailable ? undefined : `/therapists/chats/${chat.id}/generate_summary`}
              trigger={
                <Button variant="outline" size="sm" className="gap-2">
                  <MessageCircle className="h-4 w-4" aria-hidden />
                  Apri riassunto strutturato
                </Button>
              }
            />
          </div>
        </header>

        <section className="space-y-4">
          <div className="relative">
            {!chat.can_view_messages && (
              <>
                <PlaceholderMessages />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
                  <ShieldAlert className="h-6 w-6 text-destructive" aria-hidden />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-destructive">
                      Contenuto non autorizzato
                    </p>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Il cliente ha scelto di mantenere privati i messaggi di questa chat. È
                      comunque disponibile il riepilogo strutturato per l&apos;analisi clinica.
                    </p>
                  </div>
                </div>
              </>
            )}

            {chat.can_view_messages && (
              <ul className="space-y-4">
                {messages.length === 0 ? (
                  <li className="text-sm text-muted-foreground">
                    Nessun messaggio registrato in questa conversazione.
                  </li>
                ) : (
                  messages.map((message) => (
                    <MessageBubble key={message.id} message={message} name={patient.first_name} />
                  ))
                )}
              </ul>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
